import {
  getApiV1ThreeLetterCodeClientAdminsStripeCreditCard,
  getGetApiV1ThreeLetterCodeClientAdminsStripeCreditCardKey,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type { GetApiV1ThreeLetterCodeClientAdminsStripeCreditCard200 } from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import { isAxiosError } from 'axios';
import type { KeyedMutator } from 'swr';

type Args = {
  /** 企業コード */
  threeLetterCode: string;
};

/**
 * 既定カード情報の取得結果。
 *
 * - `registered`: 既定カードが登録されている
 * - `unregistered`: 未登録（404）。カード払いでない、または登録直後で Webhook 未反映
 * - `failed`: 404 以外の失敗（500 / タイムアウト / ネットワーク断）
 *
 * 「未登録」と「取得失敗」は画面の出し方が変わる（前者はカード情報行を出さないだけ、後者は
 * エラー文言を出す）ため、`null` に潰さず判別可能な形で返す。判定は呼び出し側（logics）が行う。
 */
type CardFetchResult =
  | {
      status: 'registered';
      creditCard: GetApiV1ThreeLetterCodeClientAdminsStripeCreditCard200['stripe_credit_card'];
    }
  | { status: 'unregistered' }
  | { status: 'failed' };

type ReturnType = {
  /** 既定カード情報の取得結果（登録済み / 未登録 / 取得失敗） */
  data: CardFetchResult;
  /** データ再取得用の mutate 関数 */
  mutate: KeyedMutator<CardFetchResult>;
};

type IUseGetStripeCreditCard = (args: Args) => ReturnType;

/**
 * 既定カード情報取得のエラーを、画面に出せる値へ落とす（degrade）
 *
 * 404 は正常系（カード未登録）なので `unregistered`、それ以外は `failed` に変換して
 * **例外を外へ出さない**。`suspense: true` の SWR はレンダー中に例外を投げるため、
 * 再スローすると Error Boundary の無いツリー全体がアンマウントして白画面になる。
 */
const degradeFetchError = (e: unknown): CardFetchResult =>
  isAxiosError(e) && e.response?.status === 404 ? { status: 'unregistered' } : { status: 'failed' };

/**
 * 自社 Stripe Customer の既定カード情報取得フック
 *
 * カード払いでない、または既定カードが未設定（カード登録直後で Webhook 未反映等）の場合は
 * API が 404 を返す。fetcher で 404 を `unregistered` に変換してエラー扱いにせず、
 * 呼び出し側（Container / logics）で「カード未登録」として表示を出し分けられるようにする。
 *
 * 404 以外の失敗も例外にせず `failed` として返す。ご契約情報画面は会社情報・プラン情報等の
 * 他セクションと同じ Suspense 境界を共有しており、この hook が例外を投げると画面全体が
 * 落ちるため、カード情報セクションだけのエラー表示に留められるようにする。
 */
export const useGetStripeCreditCard: IUseGetStripeCreditCard = ({ threeLetterCode }) => {
  const { data, mutate } = useFetch<CardFetchResult>(
    getGetApiV1ThreeLetterCodeClientAdminsStripeCreditCardKey(threeLetterCode),
    () =>
      // 成功側は例外を投げないため、`.then(onFulfilled).catch()` ではなく 2 引数形で
      // 「成功 → registered / 失敗 → degrade」の対応を明示する
      getApiV1ThreeLetterCodeClientAdminsStripeCreditCard(threeLetterCode).then(
        ({ stripe_credit_card }): CardFetchResult => ({
          status: 'registered',
          creditCard: stripe_credit_card,
        }),
        degradeFetchError
      ),
    { suspense: true, shouldRetryOnError: false }
  );

  return { data, mutate };
};
